import type * as ClassTypes from './types/class';
import type {IAnimationOptions} from '@/components/animation-blocks/types';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import type {IValidation} from '@/components/form/validation/types';

import Observer from './observer';
import PageLoader from '@/components/page-loader';
import popupContentTemplate from '@/components/form/form-parametric-content.twig';
import {PostMessagePopup} from '@/components/popup/popup';
import RainAnimation from '@/components/animation';
import {Utils} from './utils';
import Validation from '@/components/form/validation';

const validation: IValidation = new Validation();
const observer: IObserver = new Observer();

// Слайдеры
export const initBannerSlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "banner-slider" */ '@/sections/banner-slider')
        .then(({default: BannerSlider}: IDynamicImport) => {
            new BannerSlider().init(wrapper);
        });
};

export const initContentSlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "content-slider" */ '@/sections/content-slider')
        .then(({default: ContentSlider}: IDynamicImport) => {
            new ContentSlider().init(wrapper);
        });
};

export const initContentCarousel = (wrapper: HTMLElement, settings: any = {}): void => {
    import(/* webpackChunkName: "content-carousel" */ '@/sections/content-carousel')
        .then(({default: ContentCarousel}: IDynamicImport) => {
            new ContentCarousel().init(wrapper, settings);
        });
};

export const initArchitectureSlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "content-slider" */ '@/sections/architecture')
        .then(({default: ArchitectureSlider}: IDynamicImport) => {
            new ArchitectureSlider().init(wrapper);
        });
};

export const initCardsCarousel = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "cards-carousel" */ '@/sections/cards-carousel')
        .then(({default: CardsCarousel}: IDynamicImport) => {
            new CardsCarousel().init(wrapper);
        });
};

export const initApartmentLayoutSlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "apartment-layout" */ '@/sections/apartment-layout')
        .then(({default: ApartmentLayoutSlider}: IDynamicImport) => {
            new ApartmentLayoutSlider().init(wrapper);
        });
};

export const initSimilarFlatsSlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "similar-flats-slider" */ '@/sections/similar-flats')
        .then(({default: SimilarFlatsSlider}: IDynamicImport) => {
            new SimilarFlatsSlider().init(wrapper);
        });
};

// Sticky slider
export const initStickySlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "sticky-slider" */ '@/sections/scroll-section')
        .then(({default: StickySlider}: IDynamicImport) => {
            new StickySlider().init(wrapper);
        });
};

// Шеринг
export const initShare = (shareElements: HTMLElement[]): void => {
    import(/* webpackChunkName: "share" */ '@/components/share')
        .then(({default: Share}: IDynamicImport) => {
            shareElements.forEach((element: HTMLElement) => {
                new Share().init({
                    target: element
                });
            });
        });
};

export const insertPremisesData = (wrapper: HTMLElement, data: ClassTypes.IPopupIframeData): void => {
    const currentForm = wrapper.querySelector('.j-form') as HTMLElement;
    const container = currentForm?.querySelector('.j-form__parametric-content') as HTMLElement;

    if (!currentForm || !container) {
        return;
    }

    const fillField = currentForm.querySelector('.j-form__fill-field') as HTMLInputElement;

    if (fillField) {
        fillField.value = `${data.description}`;
    }

    Utils.clearHtml(container);
    Utils.insetContent(container, popupContentTemplate(data));
};

export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            const t: ClassTypes.IPopupFacade = new PopupFacade(options);

            t.init();
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackFormElement = popup.content.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[]): void => {
    const options = {
        removeDelay         : 300,
        headerTitle         : 'Заказать звонок',
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

export const initPostMessagePopup = (): void => {
    return new PostMessagePopup({
        closeButtonAriaLabel: 'Закрыть'
    }).init();
};

export const initAlternativeProgressPopup = (elements: HTMLElement[]): void => {
    if (elements.length) {
        import(/* webpackChunkName: "popup-facade" */ '../../components/popup/popupFacade')
            .then(({default: PopupFacade}: IDynamicImport) => {
                const options = {
                    ...{openButtons: elements}
                };

                new PopupFacade(options).init();
            });
    }
};

export const initRainAnimation = (wrapper: HTMLElement): void => {
    return new RainAnimation().init(wrapper);
};

export const initHeader = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "header" */ '@/components/header')
        .then(({default: Header}: IDynamicImport) => {
            new Header().init(wrapper);
        });
};

export const initPageLoader = (wrapper: HTMLElement): void => {
    return new PageLoader().init(wrapper);
};

export const initVideoPlayer = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "video-player" */ '@/sections/banner-video')
        .then(({default: VideoPlayer}: IDynamicImport) => {
            new VideoPlayer().init(wrapper);
        });
};

// Табы
export const initTabs = (elem: HTMLElement): void => {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            new Tabs().init({elem});
        });
};

export const initAnimateNumbers = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "animate-numbers" */ '@/components/animate-numbers')
        .then(({default: AnimateNumbers}: IDynamicImport) => {
            new AnimateNumbers().init(wrapper);
        });
};

// НОВАЯ Анимация блоков при прокрутке (GSAP)
export const initAnimation = (options: Partial<IAnimationOptions> = {blockSelector: '.j-animation__section'}): void => {
    const animationWrapper = document.querySelector('.j-animation');

    if (animationWrapper) {
        import(/* webpackChunkName: "animation" */ '@/components/animation-blocks')
            .then(({default: Animation}: IDynamicImport) => {
                return new Animation({
                    wrapper: animationWrapper,
                    ...options
                });
            });
    }
};

export const initPlanoplanWidget = (uid: string, lang: string = 'en'): void => {
    const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-brand-base') || null;

    window.Planoplan.init({
        uid,
        lang,
        width          : '100%',
        height         : '100%',
        el             : '#planoplan',
        textColor      : '#000000',
        backgroundColor: '#ffffff',
        apiLink        : 'https://flat3d.planoplan.com/api/widget',
        ...primaryColor && {
            primaryColor
        }
    });
};
